import { Box, Typography, useTheme } from "@mui/material";
import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  Position,
  getSmoothStepPath,
  getStraightPath,
} from "@xyflow/react";
import React, { useId, useState } from "react";

import { monospaceStyle } from "../../theme";
import { EntityRef } from "../../types/productPlane";
import { ChainTypical } from "../../types/valueChain";
import { ChainFlowEdge, EdgeTone } from "./chainLayout";
import { statedPairs, typicalRows } from "./chainModel";
import { PATH_COLOUR_VALUES } from "./pathColours";
import { BRANCH_RAIL } from "./treeLayout";

// Width (px) of the band along an edge that answers the pointer
const HOVER_WIDTH = 16;

// The opacity of an edge a maker highlight does not keep lit
const DIMMED_OPACITY = 0.12;

// The colour of a stated edge with nothing more to say
export const EDGE_GREY = "#6b6b6b";

// The dash of an edge into or out of a collapsed row, and of a broken one
export const COLLAPSED_DASH = "6 6";
const BROKEN_DASH = "2 4";

// The dots of an edge that rests on typical-host rows alone
export const TYPICAL_DASH = "1 4";

// The dash of the tree view's link from another host of a kind
export const CROSS_DASH = "4 4";

const nameOf = (name: string | null): string => name ?? "Unnamed product";

// The name of an end of a typical row: a product no source names as such, a
// category without a name by its id
const typicalName = (end: EntityRef): string =>
  end.name ?? (end.type === "product" ? nameOf(null) : end.id);

// What a typical row says of its two ends
const typicalWords = (row: ChainTypical): string =>
  row.relation === "part_of" ? "a part of" : "typically built into";

// The filled arrowhead of an edge, in the edge's colour. It is drawn at the
// start of the path, turned round, so that it points into the host.
const ArrowHead: React.FC<{ id: string; colour: string }> = ({
  id,
  colour,
}) => (
  <defs>
    <marker
      id={id}
      viewBox="0 0 10 10"
      refX="8"
      refY="5"
      markerWidth="5"
      markerHeight="5"
      orient="auto-start-reverse"
      markerUnits="strokeWidth"
    >
      <path d="M 0 0 L 10 5 L 0 10 Z" fill={colour} />
    </marker>
  </defs>
);

// The one edge type of the chain's canvas: the stated pairs from the
// products of one card into those of another, with a filled arrowhead
// pointing the way a part is integrated: into its host, which is always
// left of it, so every arrow on the canvas points leftwards, on both sides
// of the root. A card reached directly from one two or more
// columns nearer the root is drawn straight in the accent colour, across the
// columns between; the others in sharp orthogonal steps. An edge on an
// intermediate module's paths carries the module's colour; a suspect edge is
// drawn in the warning colour, a broken one muted and dashed, neither
// dropped. An edge with no stated pair, resting on typical-host rows alone,
// is drawn grey and dotted. An edge into or out of a collapsed row is
// dashed. Hovering shows the pairs and their articles, then the typical
// rows with where each comes from. In the tree view, the trail of open kinds
// is drawn in the accent colour, another host of a kind in its subsystem
// dashed in the link colour, and the first level of the open subsystem
// hangs on a rail down its box, with no arrowhead.
const ChainEdge: React.FC<EdgeProps<ChainFlowEdge>> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}) => {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);
  const markerId = `chain-arrow-${useId().replace(/[^A-Za-z0-9_-]/g, "")}`;
  const drawn = data?.drawn;
  const tone: EdgeTone = data?.tone ?? "plain";

  // The rail runs from the subsystem's head a little way into its box, down
  // to the card, and across into it; leftwards into a mirrored box, whose
  // head is on its right edge
  const railX =
    sourcePosition === Position.Right
      ? sourceX - BRANCH_RAIL / 2
      : sourceX + BRANCH_RAIL / 2;
  const [path, labelX, labelY] =
    data?.shape === "rail"
      ? [
          `M ${sourceX} ${sourceY} H ${railX} V ${targetY} H ${targetX}`,
          (railX + targetX) / 2,
          targetY,
        ]
      : data?.shape === "straight"
        ? getStraightPath({ sourceX, sourceY, targetX, targetY })
        : getSmoothStepPath({
            sourceX,
            sourceY,
            targetX,
            targetY,
            sourcePosition,
            targetPosition,
            borderRadius: 0,
          });

  const toneColours: Record<EdgeTone, string> = {
    broken: theme.palette.text.disabled,
    suspect: theme.palette.warning.main,
    typical: EDGE_GREY,
    direct: theme.palette.accent.main,
    path: data?.pathColour ? PATH_COLOUR_VALUES[data.pathColour] : EDGE_GREY,
    open: theme.palette.accent.main,
    cross: theme.palette.link.main,
    plain: EDGE_GREY,
  };
  const colour = hovered ? theme.palette.link.main : toneColours[tone];
  const dashed =
    tone === "broken"
      ? BROKEN_DASH
      : tone === "typical"
        ? TYPICAL_DASH
        : tone === "cross"
          ? CROSS_DASH
          : drawn?.collapsed
            ? COLLAPSED_DASH
            : undefined;
  // The arrow points into the host: the edge's source, on either side, or
  // its target in the tree of hosts. A rail has none: the edge into its
  // subsystem carries it.
  const marker = data?.shape === "rail" ? undefined : `url(#${markerId})`;
  const hostAtTarget = data?.hostAtTarget === true;
  // What hovering has to say: the pairs, the typical rows, or the edge's
  // own warning; an edge into a whole subsystem says nothing
  const said =
    drawn !== undefined &&
    (tone === "suspect" ||
      tone === "broken" ||
      statedPairs(drawn).length > 0 ||
      typicalRows(drawn).length > 0);

  return (
    <>
      <ArrowHead id={markerId} colour={colour} />
      <BaseEdge
        id={id}
        path={path}
        interactionWidth={0}
        markerStart={hostAtTarget ? undefined : marker}
        markerEnd={hostAtTarget ? marker : undefined}
        style={{
          stroke: colour,
          strokeWidth: hovered ? 2.5 : 2,
          strokeDasharray: dashed,
          // Round ends make the short dashes of a typical edge dots
          strokeLinecap: tone === "typical" ? "round" : undefined,
          opacity: data?.dimmed
            ? DIMMED_OPACITY
            : tone === "broken"
              ? 0.6
              : tone === "cross"
                ? 0.75
                : 1,
        }}
      />
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={HOVER_WIDTH}
        data-testid="chain-edge"
        data-source={drawn?.source}
        data-target={drawn?.target}
        data-shape={data?.shape}
        data-tone={tone}
        data-path-colour={data?.pathColour ?? undefined}
        data-dimmed={data?.dimmed ? "true" : "false"}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      />
      {tone === "suspect" && !hovered && (
        <EdgeLabelRenderer>
          <Box
            aria-label="Suspect edge"
            sx={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: "none",
              px: 0.5,
              borderRadius: (muiTheme) => `${muiTheme.radius.chip}px`,
              backgroundColor: "background.paper",
              border: 1,
              borderColor: "warning.main",
              color: "warning.main",
              fontSize: "0.65rem",
              lineHeight: 1.4,
              opacity: data?.dimmed ? DIMMED_OPACITY : 1,
              ...monospaceStyle,
            }}
          >
            !
          </Box>
        </EdgeLabelRenderer>
      )}
      {hovered && drawn && said && (
        <EdgeLabelRenderer>
          <Box
            role="tooltip"
            sx={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: "none",
              maxWidth: 320,
              p: 1,
              backgroundColor: "background.paper",
              border: 1,
              borderColor: "divider",
              borderRadius: (muiTheme) => `${muiTheme.radius.surface}px`,
              zIndex: 10,
            }}
          >
            {tone === "suspect" && (
              <Typography variant="caption" component="div" color="warning.main">
                Suspect: the part sits higher in the level order than its host
              </Typography>
            )}
            {tone === "broken" && (
              <Typography
                variant="caption"
                component="div"
                color="text.secondary"
              >
                Left out of the chain to break a cycle
              </Typography>
            )}
            {statedPairs(drawn).map((pair) => (
              <Typography
                key={`${pair.host.id}:${pair.part.id}`}
                variant="caption"
                component="div"
              >
                {nameOf(pair.part.name)} in {nameOf(pair.host.name)}{" "}
                <Box
                  component="span"
                  sx={{ ...monospaceStyle, color: "text.secondary" }}
                >
                  · {pair.articles} article{pair.articles === 1 ? "" : "s"}
                </Box>
              </Typography>
            ))}
            {typicalRows(drawn).map((row, index) => (
              <Typography
                key={`typical:${row.part.id}:${row.host.id}:${index}`}
                variant="caption"
                component="div"
              >
                {typicalName(row.part)} {typicalWords(row)}{" "}
                {typicalName(row.host)}{" "}
                <Box
                  component="span"
                  sx={{ ...monospaceStyle, color: "text.secondary" }}
                >
                  · {row.basis ?? row.relation} · {row.articles} article
                  {row.articles === 1 ? "" : "s"}
                </Box>
              </Typography>
            ))}
          </Box>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export default ChainEdge;
